import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Trash2, Minus, Plus, ShoppingCart, Loader2, CalendarDays, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useExperiences } from "@/hooks/useExperiences";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const StayRow = ({ item, onRemove }) => (
  <div data-testid={`cart-item-${item.key}`} className="flex items-start justify-between gap-4 rounded-lg border border-border/60 p-4">
    <div className="flex items-start gap-3">
      <CalendarDays size={18} className="text-primary mt-0.5 shrink-0" />
      <div>
        <p className="font-heading text-base font-semibold text-foreground">{item.listing_title}</p>
        <p className="font-body text-sm text-muted-foreground">
          {fmtDate(item.check_in)} → {fmtDate(item.check_out)} · {item.nights} night{item.nights > 1 ? "s" : ""} · {item.guests_count} guest{item.guests_count > 1 ? "s" : ""}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <span className="font-heading text-base font-semibold text-foreground">${item.amount.toFixed(2)}</span>
      <button data-testid={`cart-remove-${item.key}`} onClick={() => onRemove(item.key)} className="text-muted-foreground hover:text-destructive transition-colors" aria-label="Remove">
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

const ExperienceRow = ({ item, onRemove, onQty, onChildren }) => {
  const perPerson = item.pricing_option === "per_person";
  const maxQty = item.max_qty || 10;
  const kids = item.children || 0;
  const childPrice = Number(item.child_price) || 0;
  const lineTotal = item.price * item.qty + childPrice * kids;
  return (
  <div data-testid={`cart-item-${item.key}`} className="flex items-center justify-between gap-4 rounded-lg border border-border/60 p-4">
    <div className="flex items-start gap-3">
      <Sparkles size={18} className="text-accent mt-0.5 shrink-0" />
      <div>
        <p className="font-heading text-base font-semibold text-foreground">{item.name}</p>
        <p className="font-body text-sm text-muted-foreground">
          {perPerson
            ? `$${item.price.toFixed(2)} per ${childPrice > 0 ? "adult" : "person"}${childPrice > 0 ? ` · $${childPrice.toFixed(2)} per child` : ""}`
            : item.max_guests
              ? `$${item.price.toFixed(2)} group rate · up to ${item.max_guests} guests`
              : `$${item.price.toFixed(2)} each`}
        </p>
        {(item.event_date || item.film) && (
          <p data-testid={`cart-item-details-${item.key}`} className="font-body text-xs text-accent mt-0.5">
            {item.event_date ? `${item.film ? "Movie night" : "Preferred date"}: ${fmtDate(item.event_date)}` : ""}
            {item.event_date && item.film ? " · " : ""}
            {item.film ? `Film: ${item.film}` : ""}
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <div className="flex flex-col items-center">
        <div className="flex items-center gap-1 border border-border/60 rounded-full px-1">
          <button data-testid={`cart-qty-minus-${item.id}`} onClick={() => onQty(item.key, item.qty - 1)} className="p-1.5 text-muted-foreground hover:text-primary" aria-label="Decrease">
            <Minus size={13} />
          </button>
          <span data-testid={`cart-qty-${item.id}`} className="font-body text-sm w-5 text-center">{item.qty}</span>
          <button data-testid={`cart-qty-plus-${item.id}`} onClick={() => onQty(item.key, item.qty + 1)} disabled={item.qty + kids >= maxQty} className="p-1.5 text-muted-foreground hover:text-primary disabled:opacity-30" aria-label="Increase">
            <Plus size={13} />
          </button>
        </div>
        {perPerson && <span className="font-body text-[10px] text-muted-foreground mt-0.5">{childPrice > 0 ? "adults" : "guests"}{item.qty + kids >= maxQty ? ` · max ${maxQty}` : ""}</span>}
      </div>
      {perPerson && childPrice > 0 && (
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-1 border border-border/60 rounded-full px-1">
            <button data-testid={`cart-kids-minus-${item.id}`} onClick={() => onChildren(item.key, kids - 1)} className="p-1.5 text-muted-foreground hover:text-primary" aria-label="Fewer kids">
              <Minus size={13} />
            </button>
            <span data-testid={`cart-kids-${item.id}`} className="font-body text-sm w-5 text-center">{kids}</span>
            <button data-testid={`cart-kids-plus-${item.id}`} onClick={() => onChildren(item.key, kids + 1)} disabled={item.qty + kids >= maxQty} className="p-1.5 text-muted-foreground hover:text-primary disabled:opacity-30" aria-label="More kids">
              <Plus size={13} />
            </button>
          </div>
          <span className="font-body text-[10px] text-muted-foreground mt-0.5">kids</span>
        </div>
      )}
      <span className="font-heading text-base font-semibold text-foreground w-16 text-right">${lineTotal.toFixed(2)}</span>
      <button data-testid={`cart-remove-${item.key}`} onClick={() => onRemove(item.key)} className="text-muted-foreground hover:text-destructive transition-colors" aria-label="Remove">
        <Trash2 size={16} />
      </button>
    </div>
  </div>
  );
};

const UpsellStrip = ({ items, onAdd, experiences }) => {
  const inCart = new Set(items.filter((i) => i.type === "experience").map((i) => i.id));
  const suggestions = experiences.filter((e) => !inCart.has(e.id)).slice(0, 3);
  if (!suggestions.length) return null;
  return (
    <div data-testid="cart-upsell" className="mt-8">
      <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Complete Your Getaway</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {suggestions.map((exp) => (
          <div key={exp.id} className="rounded-lg border border-border/60 p-4 flex flex-col">
            <p className="font-heading text-sm font-semibold text-foreground mb-1">{exp.name}</p>
            <p className="font-body text-xs text-muted-foreground mb-3 flex-1 line-clamp-2">{exp.description}</p>
            <div className="flex items-center justify-between">
              <span className="font-heading text-base font-semibold text-primary">${exp.price}</span>
              <Button
                data-testid={`upsell-add-${exp.id}`}
                size="sm"
                variant="outline"
                className="font-body text-xs rounded-full border-primary text-primary hover:bg-primary hover:text-white"
                onClick={() => {
                  onAdd(exp);
                  toast.success(`${exp.name} added to cart`);
                }}
              >
                <Plus size={13} className="mr-1" /> Add
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CartSummaryPanel = ({ total, discount, appliedOffer, promo, setPromo, onApplyPromo, onRemovePromo, applying, form, update, error, submitting, formValid, onCheckout }) => {
  const payable = Math.max(total - discount, 0);
  return (
  <div className="border border-border/60 rounded-xl p-5 md:p-6 bg-white space-y-4 lg:sticky lg:top-24">
    <div className="space-y-1.5">
      <div className="flex justify-between font-body text-sm text-muted-foreground">
        <span>Subtotal</span>
        <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
      </div>
      {appliedOffer && discount > 0 && (
        <div className="flex justify-between font-body text-sm text-green-700">
          <span>{appliedOffer.code}</span>
          <span data-testid="cart-discount">-${discount.toFixed(2)}</span>
        </div>
      )}
      {appliedOffer && appliedOffer.type === "freebie" && (
        <p data-testid="cart-freebie" className="font-body text-xs text-green-700">
          🎁 {appliedOffer.title} — included with your booking
        </p>
      )}
      <div className="flex justify-between font-heading text-lg font-semibold text-foreground border-t border-border/60 pt-2">
        <span>Total</span>
        <span data-testid="cart-total">${payable.toFixed(2)} USD</span>
      </div>
    </div>
    {appliedOffer ? (
      <button data-testid="cart-remove-promo" onClick={onRemovePromo} className="font-body text-xs text-muted-foreground hover:text-destructive underline">
        Remove promo code {appliedOffer.code}
      </button>
    ) : (
      <div className="flex gap-2">
        <Input
          data-testid="cart-promo-input"
          placeholder="Promo code"
          value={promo}
          onChange={(e) => setPromo(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && onApplyPromo()}
          className="font-body uppercase"
        />
        <Button data-testid="cart-apply-promo" variant="outline" onClick={onApplyPromo} disabled={applying || !promo.trim()} className="font-body rounded-full shrink-0">
          {applying ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
        </Button>
      </div>
    )}
    <div className="space-y-3 border-t border-border/60 pt-4">
      <div className="grid grid-cols-2 gap-3">
        <Input data-testid="cart-first-name" placeholder="First name" value={form.first_name} onChange={update("first_name")} className="font-body" />
        <Input data-testid="cart-last-name" placeholder="Last name" value={form.last_name} onChange={update("last_name")} className="font-body" />
      </div>
      <Input data-testid="cart-email" type="email" placeholder="Email" value={form.email} onChange={update("email")} className="font-body" />
      <Input data-testid="cart-phone" type="tel" placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} className="font-body" />
    </div>
    {error && (
      <p data-testid="cart-error" className="font-body text-sm text-destructive">{error}</p>
    )}
    <Button
      data-testid="cart-checkout-button"
      onClick={onCheckout}
      disabled={!formValid || submitting}
      className="w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full h-11"
    >
      {submitting ? (
        <span className="flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Redirecting to payment...
        </span>
      ) : (
        `Checkout $${Math.max(total - discount, 0).toFixed(2)}`
      )}
    </Button>
    <p className="font-body text-[11px] text-muted-foreground text-center">
      One secure Stripe payment for everything. Stays are confirmed instantly.
    </p>
  </div>
  );
};

export default function CartPage() {
  const { items, removeItem, updateQty, updateChildren, addExperience, total } = useCart();
  const allServices = useExperiences();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [promo, setPromo] = useState("");
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [applying, setApplying] = useState(false);

  const discount = appliedOffer
    ? appliedOffer.type === "percent"
      ? Math.round(total * appliedOffer.value) / 100
      : appliedOffer.type === "fixed"
        ? Math.min(appliedOffer.value, total)
        : appliedOffer.type === "free_night"
          ? Math.min(appliedOffer.discount || 0, total)
          : 0
    : 0;

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setApplying(true);
    try {
      const res = await fetch(`${API}/api/offers/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: promo.trim(),
          subtotal: total,
          email: form.email.trim(),
          stays: stays.map((s) => ({ nights: s.nights, amount: s.amount })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Invalid promo code");
        return;
      }
      setAppliedOffer(data);
      toast.success(`${data.code} applied — ${data.title}`);
    } catch {
      toast.error("Could not validate the code. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const stays = items.filter((i) => i.type === "stay");
  const experiences = items.filter((i) => i.type === "experience");
  const formValid = form.first_name.trim() && form.last_name.trim() && /\S+@\S+\.\S+/.test(form.email);
  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleCheckout = async () => {
    if (!formValid || !items.length) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/cart/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stays: stays.map((s) => ({
            listing_id: s.listing_id,
            listing_title: s.listing_title,
            check_in: s.check_in,
            check_out: s.check_out,
            guests_count: s.guests_count,
          })),
          experiences: experiences.map((e) => ({ experience_id: e.id, qty: e.qty, event_date: e.event_date || "", film: e.film || "", pricing_option: e.pricing_option || "", children: e.children || 0 })),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          origin_url: window.location.origin,
          offer_code: appliedOffer ? appliedOffer.code : "",
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setError(data.detail || "Could not start checkout. Please try again.");
        setSubmitting(false);
        return;
      }
      window.location.href = data.url;
    } catch {
      setError("Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Your Cart | Texoma Resorts</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <section className="pt-28 pb-24 md:pt-32 min-h-[70vh]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-foreground tracking-tight mb-8 flex items-center gap-3">
              <ShoppingCart size={28} className="text-primary" /> Your Cart
            </h1>

            {!items.length ? (
              <div data-testid="cart-empty" className="text-center py-16">
                <p className="font-body text-base text-muted-foreground mb-6">
                  Your cart is empty. Add a stay or an experience to get started.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link to="/accommodations">
                    <Button data-testid="cart-browse-stays" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
                      Browse Stays
                    </Button>
                  </Link>
                  <Link to="/experiences">
                    <Button variant="outline" className="font-body rounded-full px-6 border-primary text-primary hover:bg-primary hover:text-white">
                      Browse Experiences
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 items-start">
                <div>
                  <div className="space-y-3" data-testid="cart-items">
                    {stays.map((item) => (
                      <StayRow key={item.key} item={item} onRemove={removeItem} />
                    ))}
                    {experiences.map((item) => (
                      <ExperienceRow key={item.key} item={item} onRemove={removeItem} onQty={updateQty} onChildren={updateChildren} />
                    ))}
                  </div>
                  <UpsellStrip items={items} onAdd={addExperience} experiences={allServices} />
                </div>

                <CartSummaryPanel
                  total={total}
                  discount={discount}
                  appliedOffer={appliedOffer}
                  promo={promo}
                  setPromo={setPromo}
                  onApplyPromo={applyPromo}
                  onRemovePromo={() => { setAppliedOffer(null); setPromo(""); }}
                  applying={applying}
                  form={form}
                  update={update}
                  error={error}
                  submitting={submitting}
                  formValid={formValid}
                  onCheckout={handleCheckout}
                />
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
}
